import { z } from "zod";
import type { QuickBooksCustomer } from "./entities/customer";
import type { QuickBooksInvoice } from "./entities/invoice";
import type { CompanyMetadata, MultiCompanyItem } from "./multi-company";

const companyMetadataSchema = z.object({
  realmId: z.string(),
  companyName: z.string(),
  environment: z.string(),
  connectedAt: z.string(),
  isActive: z.boolean().optional(),
});

const quickBooksEntityRecordSchema = z.record(z.string(), z.unknown());

/**
 * Items in the multi-company response are wrapped as `{ data, company }`. The
 * Zod schema accepts either shape (legacy flat records or new wrapped items)
 * and the parser normalises both forms into MultiCompanyItem<T>.
 */
const multiCompanyItemSchema = z.object({
  data: quickBooksEntityRecordSchema,
  company: companyMetadataSchema,
});

const quickBooksEntityListResponseSchema = z
  .object({
    items: z.array(z.union([multiCompanyItemSchema, quickBooksEntityRecordSchema])).optional(),
    totalCount: z.number().optional(),
    companiesCount: z.number().optional(),
    companies: z.array(companyMetadataSchema).optional(),
    errors: z
      .array(
        z.object({
          realmId: z.string(),
          error: z.string(),
        })
      )
      .optional(),
  })
  .passthrough();

const quickBooksApiErrorSchema = z
  .object({
    error: z.string().optional(),
    message: z.string().optional(),
  })
  .passthrough();

type QuickBooksEntity = QuickBooksInvoice | QuickBooksCustomer;

export type QuickBooksEntityListResponse<TEntity extends QuickBooksEntity = QuickBooksEntity> = {
  items: MultiCompanyItem<TEntity>[];
  totalCount: number;
  companiesCount: number;
  companies: CompanyMetadata[];
  errors?: Array<{ realmId: string; error: string }>;
};

export type QuickBooksPerRealmListResponse<TEntity extends QuickBooksEntity = QuickBooksEntity> = {
  items: Array<TEntity>;
  totalCount?: number;
  companies?: CompanyMetadata[];
  companiesCount?: number;
};

type ParsedItem =
  | { data: Record<string, unknown>; company: CompanyMetadata }
  | Record<string, unknown>;

function isWrappedItem(
  item: ParsedItem
): item is { data: Record<string, unknown>; company: CompanyMetadata } {
  return (
    typeof item === "object" &&
    item !== null &&
    "data" in item &&
    "company" in item &&
    typeof (item as { data: unknown }).data === "object" &&
    typeof (item as { company: unknown }).company === "object"
  );
}

function mapToWrappedItems<TEntity extends QuickBooksEntity>(
  records: ParsedItem[],
  fallbackCompany: CompanyMetadata | undefined
): MultiCompanyItem<TEntity>[] {
  const wrapped: MultiCompanyItem<TEntity>[] = [];
  for (const record of records) {
    if (isWrappedItem(record)) {
      wrapped.push({
        data: record.data as TEntity,
        company: record.company,
      });
    } else if (fallbackCompany) {
      wrapped.push({
        data: record as unknown as TEntity,
        company: fallbackCompany,
      });
    }
  }
  return wrapped;
}

function mapToFlatRecords<TEntity extends QuickBooksEntity>(records: ParsedItem[]): TEntity[] {
  return records.map((record) =>
    isWrappedItem(record) ? (record.data as TEntity) : (record as unknown as TEntity)
  );
}

export function parseQuickBooksApiError(data: unknown): { error?: string; message?: string } {
  return quickBooksApiErrorSchema.parse(data);
}

export function parseQuickBooksEntityResponse(
  data: unknown
): QuickBooksInvoice | QuickBooksCustomer {
  const record = quickBooksEntityRecordSchema.parse(data);
  return record as QuickBooksInvoice | QuickBooksCustomer;
}

/**
 * Parse a multi-company QuickBooks list payload. The caller supplies the entity
 * it requested from the route as `TEntity` — see
 * {@link parseQuickBooksPerRealmListResponse}.
 */
export function parseQuickBooksEntityListResponse<
  TEntity extends QuickBooksEntity = QuickBooksEntity,
>(data: unknown): QuickBooksEntityListResponse<TEntity> {
  const parsed = quickBooksEntityListResponseSchema.parse(data);
  const companies = (parsed.companies ?? []) as CompanyMetadata[];
  const fallbackCompany = companies[0];

  return {
    items: mapToWrappedItems<TEntity>((parsed.items ?? []) as ParsedItem[], fallbackCompany),
    totalCount: parsed.totalCount ?? parsed.items?.length ?? 0,
    companiesCount: parsed.companiesCount ?? parsed.companies?.length ?? 0,
    companies,
    errors: parsed.errors,
  };
}

/**
 * Parse a per-realm QuickBooks list payload. The caller knows which entity it
 * asked the route for, so it supplies that entity as `TEntity`; the shape check
 * itself is the same for every entity (the JSON boundary is untyped either way).
 */
export function parseQuickBooksPerRealmListResponse<
  TEntity extends QuickBooksEntity = QuickBooksEntity,
>(data: unknown): QuickBooksPerRealmListResponse<TEntity> {
  const parsed = quickBooksEntityListResponseSchema.parse(data);

  return {
    items: mapToFlatRecords<TEntity>((parsed.items ?? []) as ParsedItem[]),
    totalCount: parsed.totalCount,
    companies: (parsed.companies ?? undefined) as CompanyMetadata[] | undefined,
    companiesCount: parsed.companiesCount,
  };
}
